import { inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { API_BASE_URL } from '@app/core/tokens/api-base-url.token';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { PaginatedResponse } from '@app/core/models/shared/admin.model';
import {
  CreatePartnerNetworkRequest,
  PartnerNetworkModel,
  SearchPartnerNetworksRequest,
  UpdatePartnerNetworkRequest,
} from '@app/core/models/partner/partner-network.model';

const EMPTY_NETWORKS_PAGE: PaginatedResponse<PartnerNetworkModel> = {
  currentPageNumber: 0,
  pageSize: 0,
  totalElements: 0,
  totalPages: 0,
  isLastPage: true,
  isFirstPage: true,
  isEmpty: true,
  content: [],
};

@Injectable({
  providedIn: 'root',
})
export class PartnerNetworkService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = inject(API_BASE_URL);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translate = inject(TranslateService);

  readonly isLoading = signal(false);

  private get baseUrl(): string {
    return `${this.apiBaseUrl}/admin/partner-networks`;
  }

  getByPartnerId(partnerId: string, page = 0, size = 1000): Observable<PartnerNetworkModel[]> {
    if (!partnerId) return of([]);
    this.isLoading.set(true);
    const params = new HttpParams().set('page', String(page)).set('size', String(size));
    return this.http
      .get<PaginatedResponse<PartnerNetworkModel>>(`${this.baseUrl}/by-partner/${partnerId}`, {
        params,
      })
      .pipe(
        map(res => res?.content ?? []),
        catchError(error => {
          console.error('Ошибка при получении сети партнера:', error);
          this.translate.get('Ошибка при получении сети партнера').subscribe(msg => {
            this.snackbarService.error(msg);
          });
          return of([]);
        }),
        finalize(() => this.isLoading.set(false))
      );
  }

  searchPartnerNetworks(body: SearchPartnerNetworksRequest): Observable<PartnerNetworkModel[]> {
    this.isLoading.set(true);
    return this.http.post<PartnerNetworkModel[]>(`${this.baseUrl}/search`, body).pipe(
      catchError(error => {
        console.error('Ошибка при получении сети партнера:', error);
        this.translate.get('Ошибка при получении сети партнера').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        return of([]);
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  searchPartnerNetworksPage(
    page: number,
    size: number,
    body: SearchPartnerNetworksRequest
  ): Observable<PaginatedResponse<PartnerNetworkModel>> {
    this.isLoading.set(true);

    const params = new HttpParams().set('page', String(page)).set('size', String(size));

    return this.http
      .post<PaginatedResponse<PartnerNetworkModel>>(`${this.baseUrl}/search`, body, { params })
      .pipe(
        catchError(error => {
          console.error('Ошибка при получении сети партнера (page):', error);
          this.translate.get('Ошибка при получении сети партнера').subscribe(msg => {
            this.snackbarService.error(msg);
          });
          return of(EMPTY_NETWORKS_PAGE);
        }),
        finalize(() => this.isLoading.set(false))
      );
  }

  getById(networkId: string): Observable<PartnerNetworkModel | null> {
    if (!networkId) return of(null);
    this.isLoading.set(true);
    return this.http.get<PartnerNetworkModel>(`${this.baseUrl}/${networkId}`).pipe(
      catchError(error => {
        console.error('Ошибка при получении сети партнера:', error);
        this.translate.get('Ошибка при получении сети партнера').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        return of(null);
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  create(body: CreatePartnerNetworkRequest): Observable<PartnerNetworkModel> {
    this.isLoading.set(true);
    return this.http.post<PartnerNetworkModel>(this.baseUrl, body).pipe(
      catchError(error => {
        console.error('Ошибка при создании сети партнера:', error);
        this.translate.get('Ошибка при создании сети партнера').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  update(networkId: string, body: UpdatePartnerNetworkRequest): Observable<PartnerNetworkModel> {
    this.isLoading.set(true);
    return this.http.put<PartnerNetworkModel>(`${this.baseUrl}/${networkId}`, body).pipe(
      catchError(error => {
        console.error('Ошибка при обновлении сети партнера:', error);
        this.translate.get('Ошибка при обновлении сети партнера').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  delete(networkId: string): Observable<void> {
    this.isLoading.set(true);
    return this.http.delete<void>(`${this.baseUrl}/${networkId}`).pipe(
      catchError(error => {
        console.error('Ошибка при удалении сети партнера:', error);
        this.translate.get('Ошибка при удалении сети партнера').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  deactivateNetwork(network: PartnerNetworkModel): Observable<PartnerNetworkModel> {
    const body: UpdatePartnerNetworkRequest = {
      name: network.name,
      status: 'DEACTIVATED',
    };
    return this.update(network.id, body);
  }
}

